import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useParams} from 'react-router-dom';
import {ArtisticObject, ArtisticObjectListing} from "../../admin/artisticobjects/types/ArtisticObject";
import {handleError} from "../../common/utils/errorHandling";
import artisticObjectsService from "../../admin/artisticobjects/services/artisticObjectsService";
import {Image} from "../../admin/artisticobjects/types/Image";
import DetailsDescriptionSection from "../../common/components/DetailsDescriptionSection";
import {ImageType} from "../../admin/artisticobjects/types/ImageType";
import './ArtisticObjectDetails.scss';
import placesService from "../../admin/artisticobjects/services/placesService";
import {Place} from "../../admin/artisticobjects/types/Place";
import DetailsLayout from "../../common/components/details/DetailsLayout";
import DetailsImageSection from "../../common/components/details/DetailsImageSection";
import DetailsInfoCard from "../../common/components/details/DetailsInfoCard";
import DetailsPlanSection from "../../common/components/details/DetailsPlanSection";
import DetailsArtisticObjectsList from "../../common/components/details/DetailsArtisticObjectsList";
import DetailsIconclassList from "../../common/components/details/DetailsIconclassList";
import DetailsArtisansList from "../../common/components/details/DetailsArtisansList";
import {formatLocation} from '../../common/utils/locationParser';
import archivesCacheService from "../services/archivesCacheService";
import bibliographyItemsCacheService from "../services/bibliographyItemsCacheService";
import {AbbreviationMap} from "../../common/utils/abbreviationProcessor";
import searchService from "../services/searchService";
import {parseFootnotes} from "../../common/utils/footnoteParser";
import {useAuth} from "../../common/contexts/AuthContext";
import {UserRole} from "../../admin/users/types/User";
import {ArtisticObjectSections} from "../../admin/core/types/DictionaryItem";
import DetailsInscriptionsSignatures from "../../common/components/details/DetailsInscriptionsSignatures";
import {Section} from "../../admin/artisticobjects/types/Section";
import {getValueWithFallback} from "../../common/types/StringTranslations";
import {DEFAULT_LANGUAGE} from "../../common/i18n/config";

const filterSectionsByLanguage = (sections: Section[], currentLanguage: string): Section[] => {
  const nonEmptySections = sections.filter(section => section.content?.trim());
  const sectionsByType = new Map<string, Section[]>();

  nonEmptySections.forEach(section => {
    const typeAlias = section.type.alias;
    if (!sectionsByType.has(typeAlias)) {
      sectionsByType.set(typeAlias, []);
    }
    sectionsByType.get(typeAlias)!.push(section);
  });

  const filteredSections: Section[] = [];

  sectionsByType.forEach((sectionsOfType) => {
    const currentLangSection = sectionsOfType.find(s => s.language === currentLanguage);
    const defaultLangSection = sectionsOfType.find(s => s.language === DEFAULT_LANGUAGE);
    const firstSection = sectionsOfType[0];

    const selectedSection = currentLangSection || defaultLangSection || firstSection;
    if (selectedSection) {
      filteredSections.push(selectedSection);
    }
  });

  return filteredSections;
};

const filterAndSortMainSections = (sections: Section[], currentLanguage: string): Section[] => {
  return filterSectionsByLanguage(sections, currentLanguage)
    .filter(section => section.type.alias !== ArtisticObjectSections.ANSK && section.type.alias !== ArtisticObjectSections.SNTR)
    .sort((a, b) => {
      const order = Object.values(ArtisticObjectSections);
      const indexA = order.indexOf(a.type.alias as ArtisticObjectSections);
      const indexB = order.indexOf(b.type.alias as ArtisticObjectSections);
      return indexA - indexB;
    });
};

const ArtisticObjectDetails: React.FC = () => {
  const {t, i18n} = useTranslation();
  const {id} = useParams<{ id: string }>();
  const [artisticObject, setArtisticObject] = useState<ArtisticObject | undefined>();
  const [place, setPlace] = useState<Place | undefined>();
  const [image, setImage] = useState<Image | undefined>();
  const [childArtisticObjects, setChildArtisticObjects] = useState<ArtisticObjectListing[]>([]);
  const [placeArtisticObjects, setPlaceArtisticObjects] = useState<ArtisticObjectListing[]>([]);
  const [archiveAbbreviations, setArchiveAbbreviations] = useState<AbbreviationMap>({});
  const [bibliographyAbbreviations, setBibliographyAbbreviations] = useState<AbbreviationMap>({});
  const [footnoteReferences, setFootnoteReferences] = useState<AbbreviationMap>({});
  const navigate = useNavigate();
  const {user} = useAuth();

  const placeName = place ? getValueWithFallback(place.name, i18n.language) : undefined;

  useEffect(() => {
    if (id) {
      fetchArtisticObject(id);
      fetchChildArtisticObjects(id);
    }
  }, [id]);

  useEffect(() => {
    if (place?.publication?.id) {
      loadArchives(place.publication.id);
      loadBibliographyItems(place.publication.id);
    }
  }, [place?.publication?.id]);

  useEffect(() => {
    if (artisticObject) {
      extractFootnotes(artisticObject);
    }
  }, [artisticObject, i18n.language]);

  const fetchArtisticObject = async (objectId: string): Promise<void> => {
    await handleError(
      async () => {
        const objectData = await artisticObjectsService.getArtisticObject(objectId);
        setArtisticObject(objectData);
        const firstNonPlanImage = objectData?.images?.find(img => img.imageType !== ImageType.PLAN);
        setImage(firstNonPlanImage);
        fetchPlace(objectData.place.id);
      },
      t('common.errors.fetchingArtisticObject')
    );
  };

  const extractFootnotes = (artisticObject: ArtisticObject): void => {
    if (!artisticObject.sections) {
      setFootnoteReferences({});
      return;
    }

    const footnotesSections = artisticObject.sections.filter(
      section => section.type.alias === 'FTNT'
    );

    if (footnotesSections.length === 0) {
      setFootnoteReferences({});
      return;
    }

    const currentLangSection = footnotesSections.find(s => s.language === i18n.language);
    const defaultLangSection = footnotesSections.find(s => s.language === DEFAULT_LANGUAGE);
    const footnotesSection = currentLangSection || defaultLangSection || footnotesSections[0];

    const footnotes = parseFootnotes(footnotesSection.content);
    setFootnoteReferences(footnotes);
  };

  const fetchPlace = async (placeId: string): Promise<void> => {
    await handleError(
      async () => {
        const placeData = await placesService.getPlace(placeId);
        setPlace(placeData);
        fetchPlaceArtisticObjects(placeId);
      },
      t('common.errors.fetchingPlace')
    )
  };

  const fetchPlaceArtisticObjects = async (placeId: string): Promise<void> => {
    await handleError(
      async () => {
        const objectsData = await searchService.getArtisticObjectsByPlaceId(placeId);
        setPlaceArtisticObjects(objectsData);
      },
      t('common.errors.searchingArtisticObjects')
    );
  };

  const fetchChildArtisticObjects = async (parentId: string): Promise<void> => {
    await handleError(
      async () => {
        const objectsData = await searchService.getArtisticObjectsByParentId(parentId);
        setChildArtisticObjects(objectsData);
      },
      t('common.errors.fetchingChildObjects')
    );
  };

  const loadArchives = async (publicationId: string): Promise<void> => {
    await handleError(
      async () => {
        const archives = await archivesCacheService.getArchivesForPublication(publicationId);
        const abbrMap: AbbreviationMap = {};
        archives.forEach(archive => {
          abbrMap[archive.abbreviation] = archive.name;
        });
        setArchiveAbbreviations(abbrMap);
      },
      t('common.errors.loadingArchives')
    );
  };

  const loadBibliographyItems = async (publicationId: string): Promise<void> => {
    await handleError(
      async () => {
        const items = await bibliographyItemsCacheService.getBibliographyItemsForPublication(publicationId);
        const abbrMap: AbbreviationMap = {};
        items.forEach(item => {
          abbrMap[item.abbreviation] = item.content;
        });
        setBibliographyAbbreviations(abbrMap);
      },
      t('common.errors.loadingBibliography')
    );
  };

  const handleBackToGallery = () => {
    navigate('/gallery');
  };

  const handleThumbnailClick = (selectedImage: Image) => {
    setImage(selectedImage);
  };

  const handleChildArtisticObjectClick = (childObject: ArtisticObjectListing) => {
    navigate(`/gallery/artistic-object/${childObject.id}`);
  };

  const handlePlanLocationClick = (locationCode: string, matchingObjects: ArtisticObjectListing[]) => {
    if (matchingObjects.length > 0) {
      navigate(`/gallery/artistic-object/${matchingObjects[0].id}`);
    }
  };

  // Get all non-plan images except the currently displayed one
  const thumbnailImages = artisticObject?.images?.filter(img => img.id !== image?.id && img.imageType !== ImageType.PLAN) || [];

  // Get plan images from the place
  const planImages = place?.images?.filter(img => img.imageType === ImageType.PLAN) || [];

  const handlePlaceClick = () => {
    if (artisticObject?.place?.id) {
      navigate(`/gallery/place/${artisticObject.place.id}`);
    }
  };

  const handleParentClick = () => {
    if (artisticObject?.parent?.id) {
      navigate(`/gallery/artistic-object/${artisticObject.parent.id}`);
    }
  };

  const handleEditClick = () => {
    if (artisticObject?.id) {
      navigate(`/artistic-objects/${artisticObject.id}/edit`);
    }
  };

  const canEdit = user?.role === UserRole.ADMIN || user?.role === UserRole.REDACTOR;

  // Prepare key-value pairs for the info card
  const keyValuePairs = [
    {
      label: t('details.labels.creationTime'),
      value: artisticObject?.creationTime
    },
    {
      label: t('details.labels.dimensions'),
      value: artisticObject?.dimensions
    },
    {
      label: t('details.labels.location'),
      value: formatLocation(artisticObject?.locationCode, artisticObject?.locationDescription)
    },
    {
      label: '', // t('details.labels.parent'),
      value: artisticObject?.parent?.name,
      isClickable: !!artisticObject?.parent?.id,
      onClick: handleParentClick,
      to: artisticObject?.parent?.id ? `/gallery/artistic-object/${artisticObject.parent.id}` : undefined
    },
    {
      label: '', // t('details.labels.place'),
      value: artisticObject?.place?.name,
      isClickable: !!artisticObject?.place?.id,
      onClick: handlePlaceClick,
      to: artisticObject?.place?.id ? `/gallery/place/${artisticObject.place.id}` : undefined
    },
    // {
    //   label: t('details.labels.publication'),
    //   value: place?.publication?.name
    // }
  ];

  return (
    <div className="gallery-details bg-gray-100 py-8">
      <DetailsLayout onBackClick={handleBackToGallery}>
        <div className="lg:col-span-2 space-y-8">
          <DetailsImageSection
            mainImage={image}
            thumbnailImages={thumbnailImages}
            onThumbnailClick={handleThumbnailClick}
            altTextPrefix={getValueWithFallback(artisticObject?.name, i18n.language) || t('details.sections.artisticObjects')}
            placeholderAlt={getValueWithFallback(artisticObject?.name, i18n.language)}
          />

          {artisticObject?.sections && artisticObject.sections.length > 0 && (
            <div id="detailed-info" className="space-y-8">
              {filterAndSortMainSections(artisticObject.sections, i18n.language).map((section) => (
                <DetailsDescriptionSection
                  key={section.id}
                  title={section.type.value}
                  content={section.content}
                  archiveAbbreviations={archiveAbbreviations}
                  bibliographyAbbreviations={bibliographyAbbreviations}
                  footnoteReferences={section.type.alias !== 'FTNT' ? footnoteReferences : {}}
                />
              ))}
            </div>
          )}
        </div>

        <div id="info-section" className="space-y-6">
          <DetailsInfoCard
            title={artisticObject ? getValueWithFallback(artisticObject.name, i18n.language) : t('details.loading')}
            keyValuePairs={keyValuePairs}
            onEditClick={canEdit ? handleEditClick : undefined}
          />

          <DetailsArtisansList
            personAssignments={artisticObject?.personAssignments}
            workshopAssignments={artisticObject?.workshopAssignments}
            creatorsText={artisticObject?.creators}
            designersText={artisticObject?.designers}
          />

          <DetailsInscriptionsSignatures
            sections={artisticObject?.sections}
            currentLanguage={i18n.language}
          />

          {artisticObject?.iconclasses && artisticObject.iconclasses.length > 0 && (
            <DetailsIconclassList iconclasses={artisticObject.iconclasses}/>
          )}

          <DetailsPlanSection
            planImages={planImages}
            entityName={placeName}
            artisticObjects={placeArtisticObjects}
            onLocationClick={handlePlanLocationClick}
            highlightedLocationCode={artisticObject?.locationCode}
            placeId={place?.id}
          />

          <DetailsArtisticObjectsList
            artisticObjects={childArtisticObjects}
            onArtisticObjectClick={handleChildArtisticObjectClick}
            getLink={(obj) => `/gallery/artistic-object/${obj.id}`}
            title={t('details.sections.childArtisticObjects')}
          />
        </div>
      </DetailsLayout>
    </div>
  );
};

export default ArtisticObjectDetails;
